// GA4 events for the find-teacher flow: the questionnaire on /find-teacher and
// the parent's request page.
//
// With about twenty visits a month there is no A/B test to run, so these events
// are how the page is diagnosed. Every event carries the same flow_id and
// source, and one leave event per page load says where the visitor stopped and
// whether they had done anything at all.
//
// ⛔ Never pass free text. The situation note, the message to a teacher and the
// teacher's note stay out of every parameter.
import { trackEvent } from 'utils/analytics'

// @param {string} search the location's query string
// @returns {string} utm_source, then source, then "direct"
export function resolveSource(search) {
  const params = new URLSearchParams(search || '')

  return params.get('utm_source') || params.get('source') || 'direct'
}

function randomId() {
  return Math.random().toString(36).slice(2, 10)
}

// @param {Object} options
// @param {Function} options.send defaults to trackEvent
// @param {Function} options.now defaults to Date.now
// @param {string} options.search defaults to window.location.search
// @param {string} options.leaveEventName defaults to find_teacher_leave
export function createFindTeacherTracker({
  send = trackEvent,
  now = Date.now,
  search,
  leaveEventName = 'find_teacher_leave'
} = {}) {
  const flowId = randomId()
  const source = resolveSource(search ?? (typeof window !== 'undefined' ? window.location.search : ''))
  const pageStartedAt = now()
  let screen = 'intro'
  let step = ''
  let screenStartedAt = pageStartedAt
  let actionsCount = 0
  let lastAction = 'none'
  let answeredSteps = 0
  let completed = false
  let left = false

  const secondsSince = (time) => Math.round((now() - time) / 1000)

  const track = (name, params = {}) => {
    try {
      send(name, { ...params, flow_id: flowId, source })
    } catch {
      // Analytics must never break the questionnaire.
    }
  }

  return {
    flowId,
    source,
    track,
    // Anything the visitor did on purpose. Scrolling and focus are not actions.
    action(name) {
      actionsCount += 1
      lastAction = name
    },
    // keepTimer: the opening screen and the first question are one screen,
    // so the first interaction relabels it without restarting the clock.
    setScreen(nextScreen, nextStep = '', { keepTimer = false } = {}) {
      if (nextScreen === screen && nextStep === step) return
      screen = nextScreen
      step = nextStep
      if (!keepTimer) screenStartedAt = now()
    },
    secondsOnScreen() {
      return secondsSince(screenStartedAt)
    },
    completeStep() {
      answeredSteps += 1
    },
    markCompleted() {
      completed = true
    },
    leave() {
      if (left) return
      left = true
      track(leaveEventName, {
        screen,
        step,
        seconds_on_screen: secondsSince(screenStartedAt),
        seconds_on_page: secondsSince(pageStartedAt),
        actions_count: actionsCount,
        last_action: lastAction,
        answered_steps: answeredSteps,
        bounced: actionsCount === 0,
        completed,
        // Survives the page unloading; a plain request would be cancelled.
        transport_type: 'beacon'
      })
    }
  }
}

// Sends the leave event when the page is closed or hidden. Hidden counts
// because a phone switching apps never fires pagehide; the tracker itself
// makes sure only one leave goes out.
//
// @returns {Function} removes the listeners
export function sendLeaveOnExit(tracker) {
  const onHide = () => tracker.leave()
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') tracker.leave()
  }
  window.addEventListener('pagehide', onHide)
  document.addEventListener('visibilitychange', onVisibility)

  return () => {
    window.removeEventListener('pagehide', onHide)
    document.removeEventListener('visibilitychange', onVisibility)
  }
}
